// Pure display rules for the Stock, Uptime, Orders, Production Plan and Settings tabs. No React, no fetch.

import type { FilterValues } from "@/lib/filters";

import { messageFor, OpsError } from "./api";
import type {
  Entity,
  InflightCard,
  InflightSegment,
  IrtSegment,
  IrtVerdict,
  OrderRow,
  OrdersArtifact,
  OrdersSegment,
  PlanCount,
  ProductionArtifact,
  ProductionLine,
  ProductionPlan,
  ProductionSegment,
  Progress,
  RegistryEdit,
  RegistryEditResponse,
  RegistryRow,
  RowStatus,
  RunRow,
  StepState,
  StockArtifact,
  StockRow,
  Step,
  UptimeArtifact,
  UptimeRow,
  UptimeStatus,
} from "./api";

const RANK: Record<RowStatus, number> = { below_min: 0, unknown: 1, ok: 2, no_threshold: 3, no_sales: 4 };

export function statusRank(status: string): number {
  return status in RANK ? RANK[status as RowStatus] : 99;
}

export function statusWord(status: string): string {
  switch (status) {
    case "below_min": return "BELOW MINIMUM";
    case "unknown": return "UNKNOWN";
    case "ok": return "OK";
    case "no_threshold": return "NO MINIMUM";
    case "no_sales": return "NO SALES";
    default: return status.toUpperCase();
  }
}

export function num(s: string | null | undefined): number | null {
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Whole days LEFT, rounded down: C2 dates the outage the same way (stock_calc `_plus_days` floors). */
export function fmtDays(s: string | null): number | null {
  const n = num(s);
  return n == null ? null : Math.floor(n);
}

/** A threshold as stored, trailing zeros dropped: "40.00" → "40", "40.50" → "40.5". */
export function fmtMin(s: string | null): string | null {
  const n = num(s);
  return n == null ? null : String(n);
}

export function fmtUnits(s: string | null): string {
  const n = num(s);
  return n == null ? "Unknown" : Math.round(n).toLocaleString("en-GB");
}

/** Sales per day; a null rate is "Unknown", never a dash (invariant 3). */
export function fmtRate(s: string | null): string {
  const n = num(s);
  return n == null ? "Unknown" : n.toFixed(1);
}

export function orderRows(rows: StockRow[]): StockRow[] {
  return [...rows].sort((a, b) => {
    // C6 v1.8: an uncovered stock-out (NEW ISSUE) leads the tab whatever its runway word: one out for a whole window
    // reads NO SALES, which ranks below OK.
    const n = (b.new_issue ? 1 : 0) - (a.new_issue ? 1 : 0);
    if (n !== 0) return n;
    const r = statusRank(a.status) - statusRank(b.status);
    if (r !== 0) return r;
    const da = num(a.days_of_stock);
    const db = num(b.days_of_stock);
    if (da == null && db != null) return 1;
    if (da != null && db == null) return -1;
    if (da != null && db != null && da !== db) return da - db;
    return a.sku.localeCompare(b.sku);
  });
}

export function minSource(row: { min_days: string | null; channel_location: string }): string {
  if (row.min_days != null) return "registry override";
  if (row.channel_location.startsWith("amazon_")) return "Amazon default";
  if (row.channel_location.startsWith("shopify_")) return "Shopify default";
  return "default";
}

export function unknownReason(flag: StockRow["flag"]): string {
  if (flag === "NOT_IN_XERO") return "not found in Xero";
  if (flag === "STALE") return "Xero figure is over 8 days old";
  return "no stock reading this run";
}

/**
 * Days are rounded DOWN and the minimum is shown as stored, so the sentence can
 * never contradict the status: a below-minimum row always reads fewer days than
 * its minimum (floor(d) ≤ d < min), and with a whole-number minimum (the only
 * kind the editor writes) an OK row never does.
 */
export function runwaySentence(row: StockRow): string {
  const days = fmtDays(row.days_of_stock);
  const min = fmtMin(row.effective_min_days);
  switch (row.status) {
    case "below_min":
      return `${days} days left · min ${min} (${minSource(row)})`;
    case "ok":
      return `${days} days · min ${min}`;
    case "unknown":
      return `Unknown — ${unknownReason(row.flag)}`;
    case "no_sales":
      return "No sales history yet — no outage projected.";
    case "no_threshold":
      return days == null ? "No minimum" : `${days} days · no minimum`;
    default:
      return days == null ? "Unknown" : `${days} days`;
  }
}

export function sourceLabel(row: StockRow): string {
  if (row.flag === "NOT_IN_XERO") return "Not in Xero";
  if (row.source === "spapi") return "Amazon";
  if (row.flag === "LAGGED") return "Xero (lagged)";
  if (row.flag === "STALE") return "Xero (stale)";
  return "Xero";
}

export function velocityWindow(row: StockRow): "7d" | "30d" | null {
  // A zero rate equals both windows (C2 writes 0.0000 to all three on a no-sales row); no label then.
  if (row.velocity_used == null || num(row.velocity_used) === 0) return null;
  if (row.velocity_used === row.velocity_7d) return "7d";
  if (row.velocity_used === row.velocity_30d) return "30d";
  return null;
}

export function storeLabel(row: { channel: string; location: string }): string {
  return `${row.channel} · ${row.location}`;
}

export function impactLabel(units: string | null, days: string | null): string {
  const u = num(units);
  if (u == null || u === 0) return "";
  const d = fmtDays(days);
  return d == null ? `+${Math.round(u).toLocaleString("en-GB")}` : `+${Math.round(u).toLocaleString("en-GB")} → ${d} d`;
}

function str(v: FilterValues[string]): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

export function filterRows(rows: StockRow[], values: FilterValues): StockRow[] {
  const search = str(values.search)?.toLowerCase() ?? null;
  const product = str(values.product);
  const variant = str(values.variant);
  const location = str(values.location);
  const channel = str(values.channel);
  const store = str(values.store);
  return rows.filter((r) => {
    if (product && r.product !== product) return false;
    if (variant && r.variant !== variant) return false;
    if (location && r.location !== location) return false;
    if (channel && r.channel !== channel) return false;
    if (store && r.channel_location !== store) return false;
    if (search) {
      const hay = [r.sku, r.name, r.product, r.asin ?? ""].join(" ").toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

export type Tiles = { skus: number; belowMin: number; unknown: number; outagesSoon: number };

export function tilesFor(rows: StockRow[], runDate: string | null): Tiles {
  const limit = runDate ? Date.parse(runDate) + 30 * 86_400_000 : null;
  return {
    skus: rows.length,
    belowMin: rows.filter((r) => r.status === "below_min").length,
    unknown: rows.filter((r) => r.status === "unknown").length,
    outagesSoon: rows.filter((r) => {
      if (!r.outage_date || limit == null) return false;
      const t = Date.parse(r.outage_date);
      return !Number.isNaN(t) && t <= limit;
    }).length,
  };
}

/** The thirteen C2 v1.10 steps, in run order (`inflight` between stock and calc, `uptime` and `irt` before snapshot, `orders` and `forecast` after it). */
export const STEPS: Step[] = ["reseed", "registry", "xero_refresh", "velocity", "stock", "inflight", "calc", "uptime", "irt", "snapshot", "orders", "forecast", "tabs"];

/** Steps a running engine may not have yet (C2 v1.6 `uptime`, C2 v1.7 `irt`, C2 v1.9 `orders`, C2 v1.10 `forecast`): shown only when the run reports them. */
const REPORTED_ONLY: Step[] = ["uptime", "irt", "orders", "forecast"];

const STEP_LABEL: Record<Step, string> = {
  reseed: "reseed",
  registry: "registry",
  xero_refresh: "Xero refresh",
  velocity: "velocity",
  stock: "stock",
  inflight: "in flight",
  calc: "calc",
  uptime: "uptime",
  irt: "IRT",
  snapshot: "snapshot",
  orders: "orders",
  forecast: "forecast",
  tabs: "tabs",
};

const MARK: Partial<Record<StepState, string>> = {
  ok: " ✓",
  running: " …",
  failed: " ✗",
  partial: " (partial)",
  stale: " (stale)",
  skipped: " (skipped)",
  timeout: " (timeout)",
};

/**
 * Pending (or a state this build does not know) renders as the bare step name. The engine reports every step it runs
 * as `pending` from the first update, so a step it does not report is one its version does not have.
 */
export function progressLine(progress: Progress): string {
  return STEPS.filter((step) => !REPORTED_ONLY.includes(step) || step in progress)
    .map((step) => `${STEP_LABEL[step]}${MARK[progress[step] ?? "pending"] ?? ""}`)
    .join(" · ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PARIS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Paris",
  weekday: "short",
  day: "numeric",
  month: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const PARIS_TIME = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Paris",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function parts(format: Intl.DateTimeFormat, ms: number): Record<string, string> {
  return Object.fromEntries(format.formatToParts(ms).map((p) => [p.type, p.value]));
}

/** "Mon 28 Sep 04:00" in Europe/Paris (C2 `RUN_TZ`); null for a missing or unparseable time. */
export function parisDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const p = parts(PARIS, ms);
  return `${p.weekday} ${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.hour}:${p.minute}`;
}

/** "HH:MM" in Europe/Paris. */
export function parisTime(ms: number): string {
  const p = parts(PARIS_TIME, ms);
  return `${p.hour}:${p.minute}`;
}

const ENTITIES: Entity[] = ["LTD", "OU", "LLC"];

/**
 * C2 v1.3 C6 note: each organisation's Xero snapshot time, grouped when they
 * agree, naming any that differs or has none. Null when the artifact predates
 * C2 v1.3 (no `xero_read_at`), so nothing is claimed.
 */
export function xeroAsOf(readAt: StockArtifact["xero_read_at"]): string | null {
  if (!readAt) return null;
  const groups: Array<{ when: string | null; entities: Entity[] }> = [];
  for (const entity of ENTITIES) {
    const when = parisDateTime(readAt[entity] ?? null);
    const group = groups.find((g) => g.when === when);
    if (group) group.entities.push(entity);
    else groups.push({ when, entities: [entity] });
  }
  if (groups.length === 1 && groups[0].when == null) return "Xero stock: no snapshot (LTD, OU, LLC)";
  return `Xero stock as of ${groups.map((g) => `${g.when ?? "no snapshot"} (${g.entities.join(", ")})`).join(" · ")}`;
}

/** C2 `runs.STALE_MINUTES`: a run still `running` after this is failed by the next start. */
export const STALE_RUN_MS = 60 * 60_000;

export function isStaleRun(run: RunRow, now: number): boolean {
  const started = Date.parse(run.started_at);
  return !Number.isNaN(started) && now - started >= STALE_RUN_MS;
}

export type Banner = { kind: "none" | "running" | "failed" | "partial" | "ok"; text: string };

/**
 * `stalledRunId` is the run the refresh control stopped following; it and a run
 * past the stale rule no longer read as "in progress" (invariant 11).
 */
export function runBanner(
  artifact: StockArtifact | null,
  latestRun: RunRow | null,
  opts: { now?: number; stalledRunId?: string | null } = {},
): Banner {
  const now = opts.now ?? Date.now();
  if (!artifact && !latestRun) {
    return { kind: "none", text: "No run has completed yet. Press Refresh to run the first one." };
  }
  if (latestRun?.status === "running" && latestRun.run_id !== opts.stalledRunId && !isStaleRun(latestRun, now)) {
    return { kind: "running", text: "A run is in progress." };
  }
  if (latestRun?.status === "failed" && latestRun.run_id !== artifact?.run_id) {
    const note = latestRun.note ? ` (${latestRun.note})` : "";
    return artifact
      ? { kind: "failed", text: `The latest run failed${note}. Showing the last good run from ${artifact.run_date}.` }
      : { kind: "failed", text: `The latest run failed${note}. No run has completed yet.` };
  }
  if (artifact?.status === "partial") {
    const parts: string[] = [];
    // C2 puts the Xero line first in the run note; the banner keeps that order (C2 D12).
    const xero = artifact.segments.xero_refresh;
    if (xero && xero.status !== "skipped" && xero.status !== "ok" && xero.note) parts.push(xero.note);
    const reseed = artifact.segments.reseed;
    if (reseed?.status === "failed") parts.push(`reseed: ${reseed.error}`);
    for (const [entity, message] of Object.entries(artifact.segments.stock.failures ?? {})) parts.push(`${entity}: ${message}`);
    const velocity = artifact.segments.velocity;
    if (velocity.status === "stale") parts.push(`velocity carried from run ${velocity.carried_from ?? "unknown"}`);
    if (velocity.status === "failed") parts.push("velocity unavailable");
    // C2 v1.5: a failed Stock Movements read leaves the run partial; its note names the failure as the engine does.
    const inflight = artifact.segments.inflight;
    if (inflight?.status === "failed") parts.push(`in-flight conversions not read (${inflight.error})`);
    // C2 v1.7: the IRT step comes after the stock steps in the note; its parts close the list.
    parts.push(...irtFailures(artifact.segments.irt));
    // C2 v1.9: the Orders step runs after the snapshot; its part closes the list, as in the run note.
    parts.push(...ordersFailures(artifact.segments.orders));
    // C2 v1.10: the forecast step runs after the Orders step; its part comes last.
    parts.push(...forecastFailures(artifact.segments.forecast));
    return { kind: "partial", text: `This run was partial: ${parts.join("; ") || "one segment did not complete"}` };
  }
  return { kind: "ok", text: "" };
}

export type RegistryDraft = { min_days: string; status: "active" | "inactive"; reason: string };

export function draftFor(row: RegistryRow): RegistryDraft {
  return { min_days: fmtMin(row.min_days) ?? "", status: row.status, reason: "" };
}

/**
 * Only changed fields; blank min_days clears the override; null when nothing
 * changed. Compared as numbers: the server stores NUMERIC(8,2), so a saved "45"
 * comes back "45.00" and must not re-enable Save.
 */
export function registryEditBody(row: RegistryRow, draft: RegistryDraft): RegistryEdit | null {
  // The version rides every save, verbatim (C2 v1.4 D13): a row that moved on is refused, never overwritten.
  const body: RegistryEdit = { sku: row.sku, channel_location: row.channel_location, updated_at: row.updated_at };
  let changed = false;
  if (row.row_kind === "retail") {
    const next = draft.min_days.trim();
    const same = next === "" ? row.min_days == null : num(next) != null && num(next) === num(row.min_days);
    if (!same) {
      body.min_days = next === "" ? null : next;
      changed = true;
    }
  }
  if (draft.status !== row.status) {
    body.status = draft.status;
    changed = true;
  }
  if (!changed) return null;
  if (draft.reason.trim() !== "") body.reason = draft.reason.trim();
  return body;
}

/** Blank (use the default) or a whole number of days above zero. */
export function minDaysValid(value: string): boolean {
  const v = value.trim();
  return v === "" || /^[1-9]\d*$/.test(v);
}

/**
 * The channel default shown as the placeholder: C2 v1.4 serves it as
 * `default_min_days` on every row, so nothing here restates a constant (D9 closed).
 */
export function defaultMinDays(row: RegistryRow): string | null {
  if (row.row_kind === "case") return null;
  return fmtMin(row.default_min_days);
}

export type DeepLink = { key: string; pageIndex: number };

export function rowKey(row: { sku: string; channel_location: string }): string {
  return `${row.sku}|${row.channel_location}`;
}

/**
 * A Slack deep link names a row by `?sku=` (and `&store=` when one SKU sits on
 * several stores): the first match in the ordered, filtered rows, and the page it
 * sits on, so the tab opens on that row with its detail (C6 D3).
 */
export function deepLinkTarget(rows: StockRow[], sku: unknown, store: unknown, pageSize: number): DeepLink | null {
  if (typeof sku !== "string" || sku.trim() === "") return null;
  const wanted = sku.trim().toUpperCase();
  const wantedStore = typeof store === "string" && store !== "" ? store : null;
  const index = rows.findIndex((r) => r.sku.toUpperCase() === wanted && (wantedStore == null || r.channel_location === wantedStore));
  return index < 0 ? null : { key: rowKey(rows[index]), pageIndex: Math.floor(index / pageSize) };
}

/**
 * The sentence when a deep link names a row the table does not show: hidden by the
 * URL's own filters (the row is in the run), or not in this run at all. Never a
 * silent empty tab, and never "not in this run" for a row that is.
 */
export function deepLinkMissing(sku: unknown, store: unknown, hiddenByFilters: boolean): string | null {
  if (typeof sku !== "string" || sku.trim() === "") return null;
  const where = typeof store === "string" && store !== "" ? ` on ${store}` : "";
  if (hiddenByFilters) return `SKU ${sku.trim()}${where} is in this run but hidden by the filters. Clear the filters to see it.`;
  return `No row for SKU ${sku.trim()}${where} in this run.`;
}

const PLAIN_DATE = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Paris", day: "numeric", month: "short" });

/** "6 Oct" from an ISO date; null for a missing or unparseable one. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : PLAIN_DATE.format(ms);
}

/**
 * C2 v1.5: the In flight cell, from the row's own figures. "+120 in" for units arriving,
 * "−5 out" for units leaving, both when both; empty when no card names the row or the
 * artifact predates C2 v1.5. A null is never a zero (invariant 3).
 */
export function inflightLabel(row: StockRow): string {
  const parts: string[] = [];
  const arriving = num(row.inflight_in);
  const leaving = num(row.inflight_out);
  if (arriving != null && arriving !== 0) parts.push(`+${Math.round(arriving).toLocaleString("en-GB")} in`);
  if (leaving != null && leaving !== 0) parts.push(`\u2212${Math.round(leaving).toLocaleString("en-GB")} out`);
  return parts.join(" · ");
}

/** The Stock Movements `Account` select names the seller account; the registry names the location. */
const ACCOUNT_LOCATION: Record<string, string> = { UK: "UK", EU: "EU", AE: "MENA" };

/**
 * The cards that name this row's SKU on either side. A SKU that sits on two locations (a case
 * SKU) is told apart by the card's account, as the engine does; a card without an account
 * names every row of that SKU.
 */
export function inflightCards(segment: InflightSegment | undefined, row: { sku: string; location: string }): InflightCard[] {
  if (segment?.status !== "ok") return [];
  return segment.cards.filter((card) => {
    if (card.from_sku !== row.sku && card.to_sku !== row.sku) return false;
    const location = card.account ? ACCOUNT_LOCATION[card.account] : undefined;
    return location == null || location === row.location;
  });
}

/** "5 x KETO198-W24 → 120 x KETO198UKJJ · In progress · since 6 Oct · booked in Xero". */
export function inflightSentence(card: InflightCard): string {
  // A quantity that is not a number reads "?", never 0 (invariant 3).
  const side = (qty: string | null, sku: string | null) => {
    const n = num(qty);
    return `${n == null ? "?" : Math.round(n).toLocaleString("en-GB")} x ${sku ?? "?"}`;
  };
  const parts = [`${side(card.from_qty, card.from_sku)} \u2192 ${side(card.to_qty, card.to_sku)}`];
  if (card.status) parts.push(card.status);
  const since = shortDate(card.created);
  if (since) parts.push(`since ${since}`);
  if (card.booked) parts.push("booked in Xero");
  return parts.join(" · ");
}

/**
 * C2 v1.5: the line under the Xero snapshot line. Nothing while the engine has not read
 * the Stock Movements DB (skipped, or an older artifact), so nothing is claimed; the count
 * and what the figures rest on when it has; the failure when it could not.
 */
export function inflightLine(segment: InflightSegment | undefined): string | null {
  if (!segment || segment.status === "skipped") return null;
  if (segment.status === "failed") return `In-flight conversions could not be read this run (${segment.error}); the figures show none.`;
  if (segment.conversions === 0) return "No stock conversions in flight.";
  const parts = [`${segment.conversions} stock conversion${segment.conversions === 1 ? "" : "s"} in flight`];
  if (segment.booked) parts.push(`${segment.booked} already booked in Xero`);
  if (segment.unattributed.length) parts.push(`${segment.unattributed.length} side${segment.unattributed.length === 1 ? "" : "s"} the registry cannot place`);
  return `${parts.join(" · ")} (read ${parisDateTime(segment.read_at) ?? "this run"})`;
}

/** The S15 echo: `changed` false writes nothing and carries no audit id (C2 reseed amendment R.7). */
export function saveMessage(res: RegistryEditResponse): string {
  if (!res.changed) return "Nothing changed: the row already holds these values.";
  return res.audit_id ? `Saved. Applies on the next run. Audit ${res.audit_id}.` : "Saved. Applies on the next run.";
}

// C6 v1.7: the Uptime tab (C2 v1.6). Every figure is the engine's; nothing here recounts a status.

/** Problems first, as the engine's daily view ranks them (worst of the day): a dead listing above a failed check above a live one. */
const UPTIME_RANK: Record<UptimeStatus, number> = { INACTIVE: 0, SUPPRESSED: 1, "ACTIVE OUT OF STOCK": 2, UNKNOWN: 3, "ACTIVE IN STOCK": 4 };

export function uptimeRank(status: string): number {
  return UPTIME_RANK[status as UptimeStatus] ?? 3;
}

export function orderUptimeRows(rows: UptimeRow[], verdictOf: (row: UptimeRow) => IrtVerdict | undefined = (row) => row.irt): UptimeRow[] {
  return [...rows].sort((a, b) =>
    uptimeRank(a.status) - uptimeRank(b.status)
    // C6 v1.8: within a status, the painted verdict: NEW ISSUE first, an unlisted result (no problem) last.
    || irtRank(verdictOf(a)) - irtRank(verdictOf(b))
    || a.sku.localeCompare(b.sku)
    || a.channel_location.localeCompare(b.channel_location)
    || a.marketplace.localeCompare(b.marketplace));
}

export function filterUptimeRows(
  rows: UptimeRow[], values: FilterValues, verdictOf: (row: UptimeRow) => IrtVerdict | undefined = (row) => row.irt,
): UptimeRow[] {
  const search = str(values.search)?.toLowerCase();
  const product = str(values.product);
  const store = str(values.store);
  const marketplace = str(values.marketplace);
  const status = str(values.status);
  const irt = str(values.irt);
  return rows.filter((r) => {
    if (irt && verdictOf(r)?.check !== irt) return false;
    if (product && r.product !== product) return false;
    if (store && r.channel_location !== store) return false;
    if (marketplace && r.marketplace !== marketplace) return false;
    if (status && r.status !== status) return false;
    if (search) {
      const hay = [r.sku, r.name, r.product, r.asin ?? "", r.amazon_sku ?? ""].join(" ").toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

/** "Amazon DE": the store's channel and the marketplace the result is for (an EU row has nine). */
export function marketplaceLabel(row: { channel: string; marketplace: string }): string {
  return `${row.channel} ${row.marketplace}`;
}

/** The status word always travels (never colour-only); the tone only underlines it. */
export function uptimeTone(status: string): "destructive" | "outline" | "secondary" {
  if (status === "ACTIVE IN STOCK") return "secondary";
  if (status === "UNKNOWN") return "outline";
  return "destructive";
}

/** A yes/no flag the feed may not report: null is "not reported", never "no" (C6 invariant 3). */
export function flagWord(flag: boolean | null): string {
  return flag == null ? "not reported" : flag ? "yes" : "no";
}

/** How many listing issues the feed carried: null when none were carried, "unreadable" when the JSON cannot be read. */
export function issueCount(issuesJson: string | null): string | null {
  if (issuesJson == null || issuesJson === "") return null;
  try {
    const parsed: unknown = JSON.parse(issuesJson);
    return Array.isArray(parsed) ? String(parsed.length) : "1";
  } catch {
    return "unreadable";
  }
}

/** The pass that wrote the artifact, in the operator's words (C2 v1.6 call F: the 16:00 pass checks purchasability only). */
export function passLabel(triggerType: string): string {
  if (triggerType === "scheduled_0600") return "morning pass";
  if (triggerType === "scheduled_1600") return "16:00 pass";
  if (triggerType === "manual") return "manual refresh";
  return triggerType;
}

/**
 * The freshness stamp of the Uptime tab (REQ-UPT-005: the latest pass, morning or 16:00) and the slot it trusted
 * listing rows from, both in Europe/Paris. Null without an artifact.
 */
export function uptimeCheckedLine(artifact: UptimeArtifact | null): string | null {
  if (!artifact) return null;
  const checked = parisDateTime(artifact.run_started_at) ?? artifact.run_started_at;
  const sinceMs = artifact.since ? Date.parse(artifact.since) : Number.NaN;
  const since = Number.isNaN(sinceMs) ? "the listing check did not run" : `listing rows read since ${parisTime(sinceMs)}`;
  return `Checked ${checked} (${passLabel(artifact.trigger_type)}) · ${since}`;
}

/**
 * The Uptime tab's banner: the run in progress, the latest run failed (the last pass stays on screen), or a dark
 * listing view (every row Unknown, REQ-UPT-004). The Stock tab's own banner reads the stock segments, which an
 * afternoon pass skips, so this one reads the uptime segment only.
 */
export function uptimeBanner(
  artifact: UptimeArtifact | null,
  latestRun: RunRow | null,
  opts: { now?: number; stalledRunId?: string | null } = {},
): Banner {
  const now = opts.now ?? Date.now();
  if (!artifact && !latestRun) {
    return { kind: "none", text: "No pass has completed yet. Press Refresh to run the first one." };
  }
  if (latestRun?.status === "running" && latestRun.run_id !== opts.stalledRunId && !isStaleRun(latestRun, now)) {
    return { kind: "running", text: "A run is in progress." };
  }
  if (latestRun?.status === "failed" && latestRun.run_id !== artifact?.run_id) {
    const note = latestRun.note ? ` (${latestRun.note})` : "";
    return artifact
      ? { kind: "failed", text: `The latest run failed${note}. Showing the last pass from ${artifact.run_date}.` }
      : { kind: "failed", text: `The latest run failed${note}. No pass has completed yet.` };
  }
  const uptime = artifact?.segments.uptime;
  const irt = irtFailures(artifact?.segments.irt);
  if (uptime?.status === "failed") {
    // The dark view names itself first; an IRT failure of the same pass follows it, never hidden.
    const also = irt.length ? ` Also: ${irt.join("; ")}.` : "";
    return { kind: "partial", text: `The listing view could not be read this pass (${uptime.error ?? "no reason given"}): every row shows Unknown.${also}` };
  }
  if (irt.length) return { kind: "partial", text: `This pass was partial: ${irt.join("; ")}` };
  return { kind: "ok", text: "" };
}

/** The `sku|store|marketplace` key of one result (an EU SKU has nine). */
export function uptimeKey(row: { sku: string; channel_location: string; marketplace: string }): string {
  return `${row.sku}|${row.channel_location}|${row.marketplace}`;
}

/**
 * C6 v1.7 D5: a link names one result by `?sku=`, `&store=` and `&marketplace=` (the last two optional): the first match in
 * the ordered, filtered results and the page it sits on, so the tab opens on it with its detail, as the Stock tab's link does.
 */
export function uptimeDeepLinkTarget(rows: UptimeRow[], sku: unknown, store: unknown, marketplace: unknown, pageSize: number): DeepLink | null {
  if (typeof sku !== "string" || sku.trim() === "") return null;
  const wanted = sku.trim().toUpperCase();
  const wantedStore = typeof store === "string" && store !== "" ? store : null;
  const wantedMarket = typeof marketplace === "string" && marketplace !== "" ? marketplace.toUpperCase() : null;
  const index = rows.findIndex((r) => r.sku.toUpperCase() === wanted
    && (wantedStore == null || r.channel_location === wantedStore)
    && (wantedMarket == null || r.marketplace.toUpperCase() === wantedMarket));
  return index < 0 ? null : { key: uptimeKey(rows[index]), pageIndex: Math.floor(index / pageSize) };
}

/** The Uptime tab's sentence when a link names a result the table does not show (the Stock tab's rule, in the tab's words). */
export function uptimeDeepLinkMissing(sku: unknown, store: unknown, marketplace: unknown, hiddenByFilters: boolean): string | null {
  if (typeof sku !== "string" || sku.trim() === "") return null;
  const where = [
    typeof store === "string" && store !== "" ? ` on ${store}` : "",
    typeof marketplace === "string" && marketplace !== "" ? ` in ${marketplace.toUpperCase()}` : "",
  ].join("");
  if (hiddenByFilters) return `SKU ${sku.trim()}${where} is in this pass but hidden by the filters. Clear the filters to see it.`;
  return `No result for SKU ${sku.trim()}${where} in this pass.`;
}

// C6 v1.8: the IRT cross-check (C2 v1.7). The verdict is the engine's; nothing here matches an issue.

/** The run-note parts of a failed IRT step, in the engine's order: the read, the creates, the notice. */
export function irtFailures(segment: IrtSegment | undefined): string[] {
  if (!segment || segment.status !== "failed") return [];
  const parts: string[] = [];
  // `error` covers every source the step reads (the IRT, the Products DB, the ledger): name the step, not one source.
  if (segment.error) parts.push(`IRT cross-check could not read its sources (${segment.error})`);
  const creates = segment.create_failures?.length ?? 0;
  if (creates) parts.push(`${creates} IRT issue${creates === 1 ? "" : "s"} not created, retried next run`);
  // The 16:00 pass reads no stock rows, so a stock-out's notice is retried by the next run that finds the problem.
  if (segment.notice.startsWith("failed: ")) parts.push(`#ops_main notice failed (${segment.notice.slice("failed: ".length)}), retried by the next run that finds the problem`);
  return parts.length ? parts : ["the IRT cross-check did not complete"];
}

/** What the notice did this run, in words; a word this build does not know is shown as served. */
export function noticeWords(notice: string): string {
  if (notice.startsWith("sent: ")) return `#ops_main told (${notice.slice("sent: ".length)} new)`;
  if (notice === "skipped: nothing new") return "nothing new for #ops_main";
  if (notice === "skipped: not configured") return "#ops_main notice not set up";
  if (notice === "skipped: IRT not read") return "#ops_main not told";
  if (notice.startsWith("failed: ")) return `#ops_main notice failed (${notice.slice("failed: ".length)})`;
  return `#ops_main: ${notice}`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * Whether the rows carry verdicts worth painting: not before C2 v1.7 (no segment) and not while the step is not set up
 * (`skipped`: the engine then writes `unchecked` on every row, healthy ones included, and the line alone says so).
 */
export function irtShown(segment: IrtSegment | undefined): boolean {
  return segment != null && segment.status !== "skipped";
}

/**
 * The line under the tab's stamp, scoped to what the pass checked: the morning run and a manual refresh check dead
 * listings and stock-outs together, the 16:00 pass dead listings only (it writes the Uptime artifact alone). Nothing
 * before C2 v1.7; the step not set up; the IRT unreadable; else the counts and what #ops_main was told. Zero typed issues
 * among open ones means the engine cannot see STACK: Issues (C2 v1.7 R2-7), which the line names.
 */
export function irtLine(segment: IrtSegment | undefined, triggerType?: string): string | null {
  if (!segment) return null;
  if (segment.status === "skipped") return "IRT not checked: the cross-check is not set up on the engine yet.";
  const scope = triggerType === "scheduled_1600" ? "Dead listings (16:00 pass)" : "Dead listings and stock-outs";
  if (segment.error) return `${scope}: the IRT cross-check could not read its sources this run (${segment.error}), so every problem shows not checked.`;
  const parts = [`${plural(segment.problems, "problem")} checked against ${plural(segment.open_issues, "open IRT issue")}`];
  if (segment.matched) parts.push(`${segment.matched} covered`);
  if (segment.new) parts.push(`${segment.new} NEW ISSUE`);
  if (segment.created) parts.push(`${plural(segment.created, "IRT issue")} created`);
  const creates = segment.create_failures?.length ?? 0;
  if (creates) parts.push(`${creates} not created, retried next run`);
  if (segment.unlisted) parts.push(`${segment.unlisted} not raised (not in the listing feed)`);
  if (segment.open_issues > 0 && segment.typed_issues === 0) parts.push("issue types unreadable (STACK: Issues not shared with the engine)");
  parts.push(noticeWords(segment.notice));
  return `${scope}: ${parts.join(" · ")} (read ${parisDateTime(segment.read_at) ?? "this run"})`;
}

/** The engine's problem key of a row (C2 v1.7 `Problem.key`): `<kind>:<sku>:<store>:<marketplace or ->`. */
export function irtKey(row: { sku: string; channel_location: string; marketplace?: string }): string {
  return row.marketplace ? `listing:${row.sku}:${row.channel_location}:${row.marketplace}` : `stockout:${row.sku}:${row.channel_location}:-`;
}

/** Whether the run tried to create this row's IRT page and failed (the engine lists `<key>: <error>`, and retries). */
export function createFailed(segment: IrtSegment | undefined, key: string): boolean {
  return segment?.status === "failed" && (segment.create_failures ?? []).some((f) => f.startsWith(`${key}: `));
}

export type IrtCellParts = { badge: boolean; text: string | null; url: string | null };

/**
 * The verdict on a table row: NEW ISSUE as a badge with what exists behind it (the page this run created, a create that
 * failed and is retried, or no open issue), the covering issue as its STATUS linked to the page, the unlisted and
 * unchecked words; nothing on a row with no problem or an older artifact. `new` means no OPEN issue covers it: a page a
 * person closed while the listing is still dead (C2 v1.7 D9) reads the same, so the words never claim no page exists.
 */
export function irtCell(verdict: IrtVerdict | undefined, opts: { createFailed?: boolean } = {}): IrtCellParts {
  if (!verdict || verdict.check === "none") return { badge: false, text: null, url: null };
  switch (verdict.check) {
    case "new":
      if (verdict.url) return { badge: true, text: "IRT issue created", url: verdict.url };
      return { badge: true, text: opts.createFailed ? "IRT issue not created, retried next run" : "no open IRT issue", url: null };
    case "matched": return { badge: false, text: `IRT · ${verdict.status ?? "open"}`, url: verdict.url };
    case "unlisted": return { badge: false, text: "not raised: not in the listing feed", url: null };
    case "unchecked": return { badge: false, text: "IRT not checked", url: null };
    default: return { badge: false, text: `IRT · ${String(verdict.check)}`, url: verdict.url };
  }
}

export function irtCheckWord(check: string): string {
  switch (check) {
    case "matched": return "covered by an open IRT issue";
    case "new": return "NEW ISSUE: no open IRT issue covers it";
    case "unlisted": return "not raised: the listing is not in the feed";
    case "none": return "no problem to check";
    case "unchecked": return "not checked this run";
    default: return check;
  }
}

/** The sheet's IRT rows; `via` names a verdict read from another row (the stock-out behind an out-of-stock listing). */
export function irtRows(verdict: IrtVerdict | undefined, via?: string, opts: { createFailed?: boolean } = {}): Array<[string, string]> {
  if (!verdict) return [];
  const rows: Array<[string, string]> = [["Check", via ? `${via}: ${irtCheckWord(verdict.check)}` : irtCheckWord(verdict.check)]];
  if (verdict.title) rows.push(["Issue", verdict.title]);
  if (verdict.page_id) rows.push(["Issue status", verdict.status ?? "not read"]);
  if (verdict.created) rows.push(["IRT entry", "created by this run"]);
  else if (opts.createFailed) rows.push(["IRT entry", "not created this run, retried next run"]);
  return rows;
}

const UNCHECKED: IrtVerdict = { check: "unchecked", page_id: null, url: null, title: null, status: null, created: false };

export type ShownVerdict = { verdict: IrtVerdict; via?: string };

/**
 * C2 v1.7 D7: an `ACTIVE OUT OF STOCK` result has no verdict of its own (the stock-out is the issue, at pool grain), so
 * it shows the Stock row's verdict, `unchecked` included, and "not checked" when the Stock answer is not at hand. The
 * `via` words name the store, because one Amazon EU stock-out is one pool issue shown on each marketplace of the SKU, and
 * the time when the Stock verdict is older than this pass (after 16:00 it is the morning run's).
 */
export function stockVerdictFor(row: UptimeRow, stock: StockArtifact | null | undefined, uptimeRunId?: string): ShownVerdict | undefined {
  if (row.status !== "ACTIVE OUT OF STOCK" || (row.irt && row.irt.check !== "none")) return undefined;
  const via = `Stock-out, ${storeLabel(row)}`;
  if (!stock) return { verdict: UNCHECKED, via };
  const verdict = stock.rows.find((s) => s.sku === row.sku && s.channel_location === row.channel_location)?.irt;
  if (!verdict || verdict.check === "none") return undefined;
  const readAt = stock.segments.irt?.status === "skipped" ? undefined : stock.segments.irt?.read_at;
  const at = uptimeRunId && stock.run_id !== uptimeRunId && readAt ? parisDateTime(readAt) : null;
  return { verdict, via: at ? `${via} (as of ${at})` : via };
}

/** The verdict a result paints: its own, or the stock-out's on an out-of-stock listing; nothing before C2 v1.7 or while skipped. */
export function shownVerdict(row: UptimeRow, artifact: UptimeArtifact | null, stock: StockArtifact | null | undefined): ShownVerdict | undefined {
  if (!artifact || !irtShown(artifact.segments.irt)) return undefined;
  return stockVerdictFor(row, stock, artifact.run_id) ?? (row.irt ? { verdict: row.irt } : undefined);
}

/** The order key within a status: a NEW ISSUE first (nobody tracks it), an unlisted result last (not a problem). */
export function irtRank(verdict: IrtVerdict | undefined): number {
  if (verdict?.check === "new") return 0;
  if (verdict?.check === "unlisted") return 2;
  return 1;
}

/** The IRT filter (`f_irt`): the painted verdict's word, so a borrowed stock-out NEW ISSUE is found too. */
export const IRT_FILTER: Array<{ value: string; label: string }> = [
  { value: "new", label: "NEW ISSUE" },
  { value: "matched", label: "Covered by an IRT issue" },
  { value: "unchecked", label: "Not checked" },
];

/**
 * The selection of a tab with a link: what the operator picked, else the linked row until they close it. Pure, so the
 * open-on-link rule is tested without a browser (the sheet is a portal and never renders server-side).
 */
export function linkedSelection(selected: string | null, linkDismissed: boolean, linkedKey: string | null | undefined): string | null {
  return selected ?? (linkDismissed ? null : linkedKey ?? null);
}

// ---- C6 v1.9 the Orders tab (C2 v1.9 `tabs/orders/latest.json`) -----------------------------------------------------

/** What the Orders step left a run partial with, in the run note's words; nothing when it read orders or did not run. */
export function ordersFailures(segment: OrdersSegment | undefined): string[] {
  if (!segment) return [];
  if (segment.status === "failed") return [`orders not read (${segment.error ?? "no reason given"})`];
  if (segment.status === "ok" && segment.orders === 0) return ["no orders in the window"];
  return [];
}

export type OrdersView = "value" | "units" | "threshold";

/** The three REQ-ORD-001 views, as the `f_view` filter; a cleared or unknown view is the value view. */
export const ORDERS_VIEWS: Array<{ value: OrdersView; label: string }> = [
  { value: "value", label: "Top 20 by value" },
  { value: "units", label: "Top 20 by units" },
  { value: "threshold", label: "At or over threshold" },
];

export function ordersView(values: FilterValues): OrdersView {
  const view = str(values.view);
  return view === "units" || view === "threshold" ? view : "value";
}

/** A row's place in the shown view: its rank there, or (at or over threshold) its value rank when it has one. */
export function ordersRank(row: OrderRow, view: OrdersView): number | null {
  return view === "units" ? row.units_rank : row.value_rank;
}

/**
 * The shown view's rows: those the engine put in it, narrowed by store, currency and an order-id search, grouped by
 * channel-location and currency (a ranking never crosses currencies), then by rank, or by value for the threshold view.
 */
export function ordersRows(rows: OrderRow[], values: FilterValues): OrderRow[] {
  const view = ordersView(values);
  const store = str(values.store);
  const currency = str(values.currency);
  const search = str(values.search)?.toLowerCase();
  const inView = rows.filter((r) => {
    if (view === "value" ? r.value_rank == null : view === "units" ? r.units_rank == null : !r.over_threshold) return false;
    if (store && r.channel_location !== store) return false;
    if (currency && r.currency !== currency) return false;
    if (search && !r.order_id.toLowerCase().includes(search)) return false;
    return true;
  });
  return inView.sort((a, b) => {
    const group = a.channel_location.localeCompare(b.channel_location) || a.currency.localeCompare(b.currency);
    if (group) return group;
    if (view === "threshold") return (num(b.value) ?? 0) - (num(a.value) ?? 0) || a.created_at.localeCompare(b.created_at);
    return (ordersRank(a, view) ?? 99) - (ordersRank(b, view) ?? 99);
  });
}

function cap(word: string): string {
  return word ? word[0].toUpperCase() + word.slice(1) : word;
}

/** "Amazon DE", "Shopify EU": the order's own store (REQ-ORD-001 location). */
export function orderStore(row: { channel: string; location: string }): string {
  return `${cap(row.channel)} ${row.location.toUpperCase()}`;
}

/** "Amazon EU": a channel-location as the Store filter names it. */
export function ordersStoreLabel(channelLocation: string): string {
  const [channel, ...rest] = channelLocation.split("_");
  return `${cap(channel)} ${rest.join(" ").toUpperCase()}`;
}

/** "298" to "298.00", "25.9" to "25.90": the engine sends a value as BigQuery stored it; padded as text, never a float (R2-1). */
export function money(value: string): string {
  const [whole, frac = ""] = value.split(".");
  return `${whole}.${frac.padEnd(2, "0")}`;
}

/** The value in the order's own currency, never converted (bug #70); an unpriced order says so. */
export function orderValue(row: { value: string | null; currency: string }): string {
  return row.value == null ? "no price yet" : `${row.currency} ${money(row.value)}`;
}

function parisDay(iso: string | null | undefined): string | null {
  return parisDateTime(iso)?.replace(/ \d\d:\d\d$/, "") ?? null;
}

const PARIS_ISO_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" });

/** "2026-10-17": an instant's Paris calendar day, comparable as a string with a `run_date`. */
export function parisIsoDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : PARIS_ISO_DAY.format(ms);
}

/** The window the engine read and the newest order in it; the end is exclusive, so the last day is the day before. */
export function ordersWindowLine(artifact: OrdersArtifact | null): string | null {
  if (!artifact) return null;
  const end = Date.parse(artifact.window_end);
  const lastIso = Number.isNaN(end) ? null : new Date(end - 1).toISOString();
  const last = parisDay(lastIso);
  const latest = artifact.segment.latest_order_at;
  const newest = parisDateTime(latest) ?? "none";
  // C2 v1.9 D1: a read before the morning load has no order from the window's last day; the line says so (R1-2).
  const behind = latest && lastIso && (parisIsoDay(latest) ?? "") < (parisIsoDay(lastIso) ?? "");
  const lag = behind ? ` · no order from ${last} yet: the latest day may not have loaded, Refresh later` : "";
  return `Orders placed ${parisDay(artifact.window_start) ?? "?"} to ${last ?? "?"} (Paris days) · newest order read ${newest}${lag}`;
}

/** The thresholds the engine applied, and the currencies in the window that have none (C2 v1.9 call E, debt D2). */
export function ordersThresholdLine(artifact: OrdersArtifact | null): string | null {
  if (!artifact) return null;
  const set = Object.entries(artifact.thresholds).map(([currency, value]) => `${currency} ${value}`).join(" · ");
  const none = artifact.segment.no_threshold ?? [];
  return `Thresholds: ${set}${none.length ? ` · none set for ${none.join(", ")}` : ""}`;
}

/** DQ-2: the orders with no price yet are counted, and shown in the units view only. */
export function ordersPendingLine(artifact: OrdersArtifact | null): string | null {
  const n = artifact?.tiles.value_pending ?? 0;
  if (!n) return null;
  return `${n} order${n === 1 ? " has" : "s have"} no price yet (Amazon fills it in later): ${n === 1 ? "it shows" : "they show"} in the units view only.`;
}

/** Orders from a store the engine does not know are counted, never dropped silently (C2 v1.9 invariant, R1-3). */
export function ordersUnattributedLine(artifact: OrdersArtifact | null): string | null {
  const n = artifact?.segment.unattributed ?? 0;
  if (!n) return null;
  const stores = artifact?.segment.unknown_stores ?? [];
  return `${n} order${n === 1 ? "" : "s"} from stores the dashboard does not know${stores.length ? ` (${stores.join(", ")})` : ""} ${n === 1 ? "is" : "are"} not shown.`;
}

/** The words a tab's banner uses for its own file: none yet (and how to start), not updated by the latest run, shown from. */
type TabFileWords = { none: string; first: string; missed: string; shown: string; today: string; noNote?: string };

/**
 * A tab served from its own file, written only by a full run: nothing yet, a run in progress, a full run that left no
 * file of its own, the 16:00 pass after a missed morning file. The 16:00 pass writes neither the Orders nor the
 * Production file, so it alone never says the shown file is older.
 */
function tabFileBanner(
  artifact: { run_id: string; run_date: string } | null,
  latestRun: RunRow | null,
  words: TabFileWords,
  opts: { now?: number; stalledRunId?: string | null },
): Banner | null {
  const now = opts.now ?? Date.now();
  if (!artifact && !latestRun) return { kind: "none", text: words.none };
  if (latestRun?.status === "running" && latestRun.run_id !== opts.stalledRunId && !isStaleRun(latestRun, now)) {
    return { kind: "running", text: "A run is in progress." };
  }
  if (latestRun && latestRun.status !== "running" && latestRun.trigger_type !== "scheduled_1600" && latestRun.run_id !== artifact?.run_id) {
    // A step skipped without a failure leaves no note; a tab that knows why says so, never a bare full stop (R1-4).
    const note = latestRun.note ? ` (${latestRun.note})` : words.noNote ?? "";
    return artifact
      ? { kind: "failed", text: `${words.missed}${note}. ${words.shown} ${artifact.run_date}.` }
      : { kind: "failed", text: `${words.missed}${note}. ${words.first}` };
  }
  // The 16:00 pass after a missed morning read: the latest run is newer, yet the file is from an earlier day (R1-1).
  const latestDay = parisIsoDay(latestRun?.started_at);
  if (artifact && latestDay && artifact.run_date < latestDay) {
    return { kind: "failed", text: `${words.today} ${words.shown} ${artifact.run_date}.` };
  }
  return null;
}

/** The Orders tab's banner; a window that read no order is never "a quiet week" (C2 v1.9 R1-2). */
export function ordersBanner(
  artifact: OrdersArtifact | null,
  latestRun: RunRow | null,
  opts: { now?: number; stalledRunId?: string | null } = {},
): Banner {
  const shared = tabFileBanner(artifact, latestRun, {
    none: "No orders have been read yet. Press Refresh to run the first read.",
    first: "No orders have been read yet.",
    missed: "The latest run did not read the orders",
    shown: "Showing the orders read on",
    today: "No run today read the orders.",
  }, opts);
  if (shared) return shared;
  if (artifact && (artifact.segment.orders ?? 0) === 0) {
    return { kind: "partial", text: "This run read no orders for the window: the order data may not have loaded, so an empty tab is not a quiet week." };
  }
  return { kind: "ok", text: "" };
}

/** A route the deployed engine does not have yet answers FastAPI's own 404, with no error code: the tab says so. */
function tabErrorMessage(error: unknown, areaName: string, tab: string): string {
  if (error instanceof OpsError && error.status === 404 && !error.body?.error) {
    return `The engine does not serve the ${tab} tab yet: it appears after the engine's next release.`;
  }
  return messageFor(error, areaName);
}

/** Before the engine serves the route (C2 v1.9 not deployed) a 404 says so, never "that row no longer exists". */
export function ordersErrorMessage(error: unknown, areaName: string): string {
  return tabErrorMessage(error, areaName, "Orders");
}

// ---- C6 v1.10 the Production Plan tab (C2 v1.10 `tabs/production/latest.json`) --------------------------------------

/** What the forecast step left a run partial with; nothing when it ran or was skipped (a carried velocity already says so). */
export function forecastFailures(segment: ProductionSegment | undefined): string[] {
  if (segment?.status === "failed") return [`production plan not computed (${segment.error ?? "no reason given"})`];
  return [];
}

export type ProductionView = "produce" | "all";

/** The `f_show` filter: the months that still need a production (default), or every month with a need or a plan. */
export const PRODUCTION_VIEWS: Array<{ value: ProductionView; label: string }> = [
  { value: "produce", label: "To produce, no plan yet" },
  { value: "all", label: "Every month with a need or a plan" },
];

export function productionView(values: FilterValues): ProductionView {
  return str(values.show) === "all" ? "all" : "produce";
}

/** "Oct 2026": a month the engine sends as its first day. */
export function monthLabel(month: string): string {
  const [year, m] = month.split("-");
  return `${MONTHS[Number(m) - 1]} ${year}`;
}

/** "30 Sep 2026": a plain calendar date (a plan's target date), with its year; no time zone applies. */
export function dayLabel(date: string | null): string | null {
  if (!date) return null;
  const [year, m, d] = date.split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${year}`;
}

/** The retail units in one case of a `-W##` case SKU (the system's rule, PCOGS DBT-v1.8-1); null for a retail SKU. */
export function caseSize(sku: string): number | null {
  const m = /-W(\d+)$/.exec(sku);
  return m ? Number(m[1]) : null;
}

/**
 * The shown lines: the view's, narrowed by entity, product, month and a product or SKU search, sorted by month (the
 * monthly buckets of REQ-PRD-003), then entity, product and variant.
 */
export function productionLines(lines: ProductionLine[], values: FilterValues): ProductionLine[] {
  const view = productionView(values);
  const entity = str(values.entity);
  const product = str(values.product);
  const month = str(values.month);
  const search = str(values.search)?.toLowerCase();
  return lines
    .filter((l) => {
      if (view === "produce" && l.qty <= 0) return false;
      if (entity && l.entity_id !== entity) return false;
      if (product && l.product !== product) return false;
      if (month && l.month !== month) return false;
      if (search && ![l.sku, l.product, l.variant].some((v) => v.toLowerCase().includes(search))) return false;
      return true;
    })
    .sort((a, b) => a.month.localeCompare(b.month) || a.entity_id.localeCompare(b.entity_id)
      || a.product.localeCompare(b.product) || a.variant.localeCompare(b.variant));
}

/** The shown lines in month buckets, in month order; a month with no shown line has no bucket. */
export function productionBuckets(lines: ProductionLine[]): Array<{ id: string; title: string; rows: ProductionLine[] }> {
  const buckets: Array<{ id: string; title: string; rows: ProductionLine[] }> = [];
  for (const line of lines) {
    const last = buckets[buckets.length - 1];
    if (last?.id === line.month) last.rows.push(line);
    else buckets.push({ id: line.month, title: monthLabel(line.month), rows: [line] });
  }
  return buckets;
}

const units = (n: number) => `${n.toLocaleString("en-GB")} unit${n === 1 ? "" : "s"}`;
const cases = (n: number) => `${n.toLocaleString("en-GB")} case${n === 1 ? "" : "s"}`;

/**
 * The whole cases each line opens, keyed by `forecast_line_id`. Cases open whole, so they round up on the group's
 * cumulative convert units across every served line (as the engine rounds `qty`), never per month: per month, 31 and
 * 209 units of 24 would open 2 + 9 cases from a 10-case pool (R1-1). Read from the artifact's lines, not the shown ones.
 */
export function convertCases(lines: ProductionLine[]): Map<string, number> {
  const out = new Map<string, number>();
  const groups = new Map<string, ProductionLine[]>();
  for (const line of lines) {
    const key = `${line.entity_id}|${line.product}|${line.variant}`;
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }
  for (const group of groups.values()) {
    let total = 0;
    let opened = 0;
    for (const line of [...group].sort((a, b) => a.month.localeCompare(b.month))) {
      const size = caseSize(line.sku);
      total += line.convert_units;
      const due = size ? Math.ceil(total / size) : 0;
      out.set(line.forecast_line_id, due - opened);
      opened = due;
    }
  }
  return out;
}

/**
 * Convert first (REQ-PRD-005): the units the month takes from the case pool, as the whole cases to open (from
 * `convertCases`); the surplus of a case stays on the shelf as stock (C2 v1.10 call E).
 */
export function convertWords(line: ProductionLine, casesToOpen: number | undefined): string | null {
  if (line.convert_units <= 0) return null;
  if (!caseSize(line.sku)) return `Convert ${units(line.convert_units)} first`;
  const open = casesToOpen ?? 0;
  return open > 0
    ? `Convert ${cases(open)} of ${line.sku} first (${units(line.convert_units)})`
    : `Convert ${units(line.convert_units)} first, from the case opened the month before`;
}

/** What to produce after converting: `qty` cases of the case SKU, and the retail units they hold. */
export function produceWords(line: ProductionLine): string | null {
  if (line.qty <= 0) return null;
  return `Produce ${cases(line.qty)} of ${line.sku} (${units(line.produce_units)})`;
}

/** "MCT · 500ml": a line's or a plan's product, as the table names it. */
export function productLabel(row: { product: string | null; variant: string | null }): string {
  return row.product ? `${row.product}${row.variant ? ` · ${row.variant}` : ""}` : "no product";
}

/** How the forecast counted an open plan (C2 v1.10 call D), in words a planner acts on. */
export function planCountWords(plan: ProductionPlan): string {
  const due = dayLabel(plan.target_date) ?? "";
  switch (plan.counts_as) {
    case "inbound": return "In production: counted as stock now";
    case "overdue": return `Overdue (due ${due}): counted as stock now`;
    case "future": return `Arrives ${plan.month ? monthLabel(plan.month) : due}`;
    case "beyond_horizon": return `Due ${due}, after the six months: not counted`;
    case "undated": return "No target date: not counted until it has one";
    case "unmatched": return `Matches no product of ${plan.entity_id}: not counted`;
  }
}

/** Plans that need the planner's attention: the forecast counted them late, or not at all. */
export const PLAN_CHECK: PlanCount[] = ["overdue", "undated", "unmatched"];
const PLAN_ORDER: PlanCount[] = ["overdue", "unmatched", "undated", "inbound", "future", "beyond_horizon"];

/** The open plans, narrowed by entity, product and the search, the ones to check first, then by target date and key. */
export function productionPlans(plans: ProductionPlan[], values: FilterValues): ProductionPlan[] {
  const entity = str(values.entity);
  const product = str(values.product);
  const search = str(values.search)?.toLowerCase();
  return plans
    .filter((p) => {
      if (entity && p.entity_id !== entity) return false;
      if (product && p.product !== product) return false;
      if (search && ![p.production_key, p.sku, p.product ?? ""].some((v) => v.toLowerCase().includes(search))) return false;
      return true;
    })
    .sort((a, b) => PLAN_ORDER.indexOf(a.counts_as) - PLAN_ORDER.indexOf(b.counts_as)
      || (a.target_date ?? "9999").localeCompare(b.target_date ?? "9999") || a.production_key.localeCompare(b.production_key));
}

/**
 * "12.5000" to "12.5", "240.0000" to "240", "1200" to "1,200": a NUMERIC(18,4) quantity as served, trailing zeros
 * trimmed and thousands grouped, never rounded (a 12.5-unit plan is not 13; R2-1).
 */
export function fmtQty(value: string): string {
  const [whole, frac = ""] = value.split(".");
  const kept = frac.replace(/0+$/, "");
  return `${Number(whole).toLocaleString("en-GB")}${kept ? `.${kept}` : ""}`;
}

/** A plan's quantity in its own SKU, and in retail units when the SKU is a case. */
export function planQuantity(plan: ProductionPlan): string {
  const qty = fmtQty(plan.qty);
  return plan.units === plan.qty ? qty : `${qty} (${fmtQty(plan.units)} units)`;
}

/** The six months the forecast covers and the run it was sized from. */
export function productionHorizonLine(artifact: ProductionArtifact | null): string | null {
  if (!artifact || artifact.months.length === 0) return null;
  const first = monthLabel(artifact.months[0]);
  const last = monthLabel(artifact.months[artifact.months.length - 1]);
  const rows = artifact.segment.sized_rows ?? 0;
  return `Six months, ${first} to ${last} · sized from ${rows} stock row${rows === 1 ? "" : "s"} of the run of ${artifact.run_date}`;
}

const UNSIZED_WORDS: Record<string, string> = { no_stock: "no stock read", no_velocity: "no sales speed" };

/** The rows the forecast could not size are named, so a missing need is never read as none (C2 v1.10 call B). */
export function productionUnsizedLine(artifact: ProductionArtifact | null): string | null {
  const rows = artifact?.unsized ?? [];
  if (!rows.length) return null;
  const named = rows.map((r) => `${r.sku} ${ordersStoreLabel(r.channel_location)} (${UNSIZED_WORDS[r.reason] ?? r.reason})`).join(", ");
  return `${rows.length} row${rows.length === 1 ? " was" : "s were"} not sized, so ${rows.length === 1 ? "its" : "their"} need is missing: ${named}.`;
}

/** Resold ranges have no case SKU and are bought, not produced (C2 v1.10 call G, debt D10): counted, never shown as need. */
export function productionNotProducedLine(artifact: ProductionArtifact | null): string | null {
  const n = artifact?.segment.not_produced_groups ?? 0;
  if (!n) return null;
  return `${n} resold product${n === 1 ? " is" : "s are"} bought in, not produced, so ${n === 1 ? "its" : "their"} need is not shown here.`;
}

/** "Plan a production": the Production COGS Runs home for the line's entity, as router link props; the dashboard never writes a plan. */
export function planLink(entityId: string) {
  return {
    to: "/app/$area/$module/$tab" as const,
    params: { area: "margin_engine", module: "pcogs", tab: "runs" },
    search: { f_entity: entityId },
  };
}

/** The Production Plan tab's banner. A failed or skipped forecast writes no file, so the run's note says why. */
export function productionBanner(
  artifact: ProductionArtifact | null,
  latestRun: RunRow | null,
  opts: { now?: number; stalledRunId?: string | null } = {},
): Banner {
  return tabFileBanner(artifact, latestRun, {
    none: "No production plan has been computed yet. Press Refresh to run the first forecast.",
    first: "No production plan has been computed yet.",
    missed: "The latest run did not update the production plan",
    shown: "Showing the plan computed on",
    today: "No run today updated the production plan.",
    noNote: " (the forecast did not run)",
  }, opts) ?? { kind: "ok", text: "" };
}

/** Before the engine serves the route (C2 v1.10 not deployed) a 404 says so. */
export function productionErrorMessage(error: unknown, areaName: string): string {
  return tabErrorMessage(error, areaName, "Production Plan");
}
